import { useState, useEffect } from "react";
import API from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import { Switch } from "../ui/switch";
import { ThreadList } from "../notes/ThreadList";
import { ChatBubble } from "../notes/ChatBubble";
import { NoteComposer } from "../notes/NoteComposer";
import { useBrandName } from "./PortalBrand";
import { toast } from "sonner";
import { Check, Checks } from "@phosphor-icons/react";

/* Client-portal side of the shared thread: team notes marked as shared, the client's own replies and key updates. */
export function ClientNotesThread({ items = [], postUrl, folder = "client-notes", canMessage = false, onSent, testPrefix = "comment" }) {
  const { user } = useAuth();
  const teamName = useBrandName();
  const [showMilestones, setShowMilestones] = useState(true);
  const hasMilestones = items.some((i) => i.kind === "milestone");
  const readUrl = postUrl ? postUrl.replace(/\/comments$/, "/read") : "";

  useEffect(() => {
    if (readUrl) API.post(readUrl).catch(() => {});
  }, [readUrl]);

  const send = async ({ text, attachments }) => {
    await API.post(postUrl, { text, attachments });
    toast.success("Sent to our team");
    onSent?.();
  };

  return (
    <div className="space-y-3" data-testid={`${testPrefix}-thread-section`}>
      <div className="flex items-center justify-between gap-2 flex-wrap text-[10px] text-muted-foreground">
        <span className="inline-flex items-center gap-2" data-testid={`${testPrefix}-ticks-legend`} title="Delivery status on your messages">
          <span className="inline-flex items-center gap-0.5"><Check size={11} weight="bold" className="text-rose-500" /> sent</span>
          <span className="inline-flex items-center gap-0.5"><Checks size={11} weight="bold" className="text-amber-500" /> received</span>
          <span className="inline-flex items-center gap-0.5"><Checks size={11} weight="bold" className="text-emerald-500" /> read</span>
        </span>
        {hasMilestones && (
          <label className="flex items-center gap-1.5 cursor-pointer">
            Show updates <Switch checked={showMilestones} onCheckedChange={setShowMilestones} className="scale-90" data-testid={`${testPrefix}-milestones-switch`} />
          </label>
        )}
      </div>
      <ThreadList
        items={items}
        showMilestones={showMilestones}
        testPrefix={testPrefix}
        maxHeight="max-h-[460px]"
        emptyText={canMessage ? "No messages yet. Ask a question or share information with our team below." : "No messages yet."}
        renderNote={(n) => (
          <ChatBubble key={n.id} item={n} mine={n.user_role === "client"} viewer="client" teamName={teamName} myUserId={user?._id} testPrefix={testPrefix} />
        )}
      />
      {canMessage && <NoteComposer onSend={send} folder={folder} mentions={false} placeholder="Write a message for our team…" sendLabel="Send" testPrefix={testPrefix} />}
    </div>
  );
}
